import { isNil, omit } from "lodash";
import { JSONPath } from "jsonpath-plus";

import {
  ValidationKey,
  CustomizationValue,
  CustomizePlanCoverage,
  PolicyCustomizationForm,
  mapGraphQLValuesToFormValues,
  mapValidationKeyToAccessor,
} from "@covertree/new-business-forms";
import {
  CoverageMineSubsidenceScheduledItem,
  CoveragePackageKeys,
  CoverageScheduledPersonalPropertyItem,
  ExtraCoverageKeys,
  PolicyLevelCoverages,
  UnitLevelCoverages,
  extraCoverageKeys,
  namesOfDeductibles,
} from "@covertree/new-business-graphql";
import { sortBasedOnAnotherArr } from "@covertree/utils";
import {
  customizablePackageCoverageAccessors,
  ExtraCoverageAccessor,
  extraCoverageAccessors,
  getExtraCoveragesMap,
  getPackageCoveragesMap,
  mapDollarsEnumToNumber,
  mapSettlementOptionToDisplayValue,
  PackageCoverageAccessor,
  ScreenWithCoverages,
} from "@covertree/new-business-mappings";

type BaseCoverageWithAccessor = {
  validationKey: ValidationKey;
  value?:
    | CustomizationValue
    | CoverageScheduledPersonalPropertyItem[]
    | CoverageMineSubsidenceScheduledItem[];
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueCustomization: any;
  valueIsHidden: boolean;
  valueIsCustomizable: boolean;
  __typename?: string;
};

const splitCoveragesByAccessor = ([key, coverageObj]: [
  string,
  (
    | UnitLevelCoverages[Exclude<keyof UnitLevelCoverages, "__typename">]
    | PolicyLevelCoverages[Exclude<keyof PolicyLevelCoverages, "__typename">]
  ),
]): BaseCoverageWithAccessor[] | BaseCoverageWithAccessor => {
  if (coverageObj.__typename === "CoverageDeductibles") {
    return namesOfDeductibles.map((deductibleName) => {
      return {
        validationKey: `${key}_${deductibleName}` as ValidationKey,
        value: mapDollarsEnumToNumber(coverageObj[deductibleName]),
        valueCustomization: coverageObj[`${deductibleName}Customization`],
        valueIsHidden: coverageObj[`${deductibleName}IsHidden`],
        valueIsCustomizable: coverageObj[`${deductibleName}IsCustomizable`],
        __typename: coverageObj.__typename,
      };
    });
  }

  if (coverageObj.__typename === "CoverageWithSettlementOption") {
    const secondaryKeys = [...(["value", "settlementOption"] as const)];
    return secondaryKeys.map((secondaryKey) => {
      return {
        validationKey: `${key}_${secondaryKey}` as ValidationKey,
        value:
          secondaryKey === "settlementOption"
            ? mapSettlementOptionToDisplayValue(coverageObj[secondaryKey])
            : coverageObj[secondaryKey],
        valueCustomization: coverageObj[`${secondaryKey}Customization`],
        valueIsHidden: coverageObj[`${secondaryKey}IsHidden`],
        valueIsCustomizable: coverageObj[`${secondaryKey}IsCustomizable`],
        __typename: coverageObj.__typename,
      };
    });
  }

  const secondaryKey = "value";
  const validationKey = `${key}_${secondaryKey}`;

  const coverage = coverageObj as UnitLevelCoverages[Exclude<
    keyof UnitLevelCoverages,
    "__typename" | "deductibles"
  >];

  return {
    validationKey: validationKey as ValidationKey,
    value: validationKey.includes("value")
      ? mapGraphQLValuesToFormValues(
          coverageObj as UnitLevelCoverages[Exclude<
            keyof UnitLevelCoverages,
            "__typename" | "deductibles"
          >]
        )
      : coverageObj[secondaryKey as keyof typeof coverageObj],
    valueCustomization:
      coverage[`${secondaryKey}Customization` as keyof typeof coverageObj],
    valueIsHidden: coverageObj[
      `${secondaryKey}IsHidden` as keyof typeof coverageObj
    ] as unknown as boolean,
    valueIsCustomizable: coverageObj[
      `${secondaryKey}IsCustomizable` as keyof typeof coverageObj
    ] as unknown as boolean,
    __typename: coverageObj.__typename,
  };
};

const mapCoverageToUIFormat = (
  {
    validationKey,
    value: defaultValueFromOptions,
    valueIsCustomizable,
    valueCustomization,
    valueIsHidden,
    __typename,
  }: BaseCoverageWithAccessor,
  currentDefaultValues: PolicyCustomizationForm,
  revisionDependentAccessors: string[]
): CustomizePlanCoverage | undefined => {
  const packageCoverageMap = getPackageCoveragesMap(
    ScreenWithCoverages.PlanDetails
  );

  const extraCoverageMap = getExtraCoveragesMap(
    ScreenWithCoverages.PlanDetails
  );

  const key = validationKey.split("_")[0] as
    | ExtraCoverageKeys
    | CoveragePackageKeys;

  const accessor = mapValidationKeyToAccessor(validationKey);

  const details = extraCoverageAccessors.includes(accessor)
    ? extraCoverageMap[accessor as ExtraCoverageAccessor]
    : packageCoverageMap[accessor as PackageCoverageAccessor];

  if (
    !details ||
    valueIsHidden ||
    defaultValueFromOptions == null ||
    (!defaultValueFromOptions &&
      defaultValueFromOptions !== 0 &&
      accessor !== ExtraCoverageAccessor.RoofPaymentSchedule) ||
    revisionDependentAccessors.includes(validationKey.split("_")[0])
  )
    return undefined;

  const isPolicyLevelCoverage = [
    "scheduledPersonalProperty",
    "identityFraudExpense",
  ].includes(key);

  const defaultValue =
    JSONPath({
      path: isPolicyLevelCoverage
        ? `policyLevelCoverages.${validationKey}`
        : `units[0].unitLevelCoverages.${validationKey}`,
      json: currentDefaultValues,
    })?.[0] || defaultValueFromOptions;

  const valueFormats = details.getValueFormats(defaultValue);

  const minMaxOptions =
    !isNil(valueCustomization?.min) && !isNil(valueCustomization?.max)
      ? [valueCustomization.min as number, valueCustomization.max as number]
      : undefined;

  const options = valueCustomization?.options ||
    minMaxOptions || [
      // If not-customizable coverages come without options, form shows this value
      { value: defaultValue, label: valueFormats?.value ?? defaultValue },
    ];

  return {
    validationKey,
    accessor: accessor as ExtraCoverageAccessor,
    fieldType: valueCustomization?.__typename || __typename,
    disabled: !valueIsCustomizable,
    options,
    step: valueCustomization?.step,
    min: valueCustomization?.min,
    max: valueCustomization?.max,
    details: {
      image: details.image,
      getName: details.getName,
      getDescription: details.getDescription,
      getTooltip: details.getTooltip,
      valueFormats,
    },
    isExtraCoverage: [...extraCoverageKeys, "identityFraudExpense"].includes(
      key as ExtraCoverageKeys
    ),
    defaultValue,
  };
};

export const prepareDataToRender = (
  currentDefaultValues: PolicyCustomizationForm,
  unitLevelCoverages?: UnitLevelCoverages,
  policyLevelCoverages?: PolicyLevelCoverages,
  revisionDependentAccessors: string[] = []
): CustomizePlanCoverage[] => {
  if (!unitLevelCoverages || !policyLevelCoverages)
    throw new Error("unitLevelCoverages and policyLevelCoverages are required");

  const coverages = Object.entries(
    omit({ ...unitLevelCoverages, ...policyLevelCoverages }, "__typename")
  ).flatMap(splitCoveragesByAccessor);

  return coverages
    .map((coverage) =>
      mapCoverageToUIFormat(
        coverage,
        currentDefaultValues,
        revisionDependentAccessors
      )
    )
    .filter(Boolean) as CustomizePlanCoverage[];
};

export const getPackageCoveragesToRender = (
  coverages: CustomizePlanCoverage[]
): CustomizePlanCoverage[] =>
  coverages
    .filter(({ isExtraCoverage }) => !isExtraCoverage)
    .sort((item1, item2) =>
      sortBasedOnAnotherArr(item1.accessor, item2.accessor, [
        ...customizablePackageCoverageAccessors,
      ])
    );
